import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Droplets, Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#F3F4F1]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#064E3B] flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-lg font-semibold">IV Therapy Finder</span>
          </div>
          <h1 className="font-display text-3xl font-semibold text-stone-900">
            {done ? "Password updated" : "Set a new password"}
          </h1>
          <p className="text-stone-600 mt-1 text-sm">
            {done ? "Redirecting you to sign in..." : "Choose a new password for your IV Therapy Finder account."}
          </p>
        </div>

        {!token ? (
          <div className="bg-white rounded-2xl border border-rose-200 p-7 text-center shadow-sm" data-testid="reset-invalid-card">
            <p className="text-sm text-stone-700">This reset link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="inline-block mt-4 text-sm font-semibold text-[#064E3B] hover:underline">
              Request a new link →
            </Link>
          </div>
        ) : done ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-7 text-center shadow-sm" data-testid="reset-done-card">
            <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
            <p className="text-sm text-stone-700">Your password has been updated successfully.</p>
            <Link to="/login" className="inline-block mt-5 text-sm font-semibold text-[#064E3B] hover:underline" data-testid="signin-now-link">
              Sign in now →
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-200 p-7 space-y-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs text-stone-500 bg-stone-50 rounded-lg px-3 py-2">
              <KeyRound className="w-3.5 h-3.5" /> This link expires 1 hour after it was sent.
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">New password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                data-testid="reset-password-new"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Confirm password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your new password"
                data-testid="reset-password-confirm"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="reset-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full bg-[#064E3B] hover:bg-[#053d2f]" data-testid="reset-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
